import React from "react";
import "./RewardCard.css";

// Circle Coins glyph: the round Blue Tokai mark, the same coin the app shows.
export const CoinIcon = ({ size = 18 }) => (
  <img className="reward-coin" src="/310x3102.png" alt="" width={size} height={size} draggable={false} />
);

// Used when a reward has no icon configured: a simple cup in the card colour.
const FallbackIcon = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" className="reward-card-fallback-icon">
    <path d="M5 8h11v6a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4V8zm11 1h1.5a2.5 2.5 0 0 1 0 5H16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 3.5c0 1 .8 1.2.8 2.2M11.5 3.5c0 1 .8 1.2.8 2.2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
    <rect x="5" y="10.5" width="14" height="9.5" rx="2.2" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BagIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
    <path d="M5.5 8.5h13l-1 11h-11l-1-11Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M9 8.5V7a3 3 0 0 1 6 0v1.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Small spinning ring used while a reward is being applied/removed, so the tap
// clearly registers as "working on it" instead of a silent pause.
const Spinner = ({ size = 15 }) => (
  <span className="reward-spinner" style={{ width: size, height: size }} aria-hidden="true" />
);

/**
 * Why a reward can't be used yet, in plain words. Two situations a first-time
 * customer can understand: not enough Circle Coins (say how many more), or the
 * cart has nothing the reward can be used on. The backend's own `reason` is only
 * a fallback for anything else, with "points" renamed to the "Circle Coins" the
 * rest of the screen uses.
 */
const lockInfo = (offer, coins) => {
  const cost = Number(offer.cost) || 0;
  const balance = Number(coins);
  const known = Number.isFinite(balance) && coins !== "" && coins !== null && coins !== undefined;
  if (known && cost > 0 && balance < cost) {
    return {
      kind: "coins",
      balance,
      cost,
      need: cost - balance,
      percent: Math.max(4, Math.min(100, Math.round((balance / cost) * 100))),
    };
  }
  const reason = String(offer.reason || "").trim();
  const generic = !reason || /no eligible|eligible product/i.test(reason);
  return {
    kind: "cart",
    text: generic
      ? "Add an eligible item to your cart to use this"
      : reason.replace(/circle points?/gi, "Circle Coins"),
    enoughCoins: known && balance >= cost,
  };
};

const DEFAULT_COLOR = "#1B3C59";
const tint = (url) => (url ? { WebkitMaskImage: `url("${url}")`, maskImage: `url("${url}")` } : undefined);

/**
 * One Circle Reward, laid out like the app's reward card: a colour panel on the
 * left with the round reward icon straddling its edge, the name and an
 * Apply / Applied pill, the description, a divider, then the coin cost; the
 * animal artwork sits on the right, tinted to the card colour.
 * Colour and artwork come from the API (deal_card_styles), so they match the app.
 * Cards are stacked in a vertical list (no horizontal scrolling).
 */
export default function RewardCard({ offer, applied, applying, saving, coins, onApply, onRemove }) {
  const disabled = !applied && offer.active === false;
  const color = offer.cardColor || DEFAULT_COLOR;
  const state = applied ? "applied" : disabled ? "disabled" : "available";
  const lock = disabled ? lockInfo(offer, coins) : null;

  return (
    <div className={`reward-card reward-card-${state} ${applying ? "reward-card-busy" : ""}`} style={{ "--reward-color": color }} aria-busy={applying || undefined}>
      {applying && (
        <div className="reward-card-overlay" aria-hidden="true">
          <Spinner size={26} />
        </div>
      )}
      <div className="reward-card-main">
        <div className="reward-card-strip" aria-hidden="true">
          <img className="reward-card-mark" src="/btc-mark-white.png" alt="" draggable={false} />
        </div>
        <span className="reward-card-icon" aria-hidden="true">
          {offer.icon ? <span className="reward-card-glyph" style={tint(offer.icon)} /> : <FallbackIcon />}
        </span>

        <div className="reward-card-body">
          <div className="reward-card-head">
            <h4 className="reward-card-title">{offer.name}</h4>
            {applied ? (
              <span className="reward-card-pill reward-card-pill-applied">Applied</span>
            ) : disabled ? (
              // Not usable yet: a "Locked" tag instead of a missing button, with the
              // plain-words way to unlock it shown under the cost (below).
              <span className="reward-card-pill reward-card-pill-locked">
                <LockIcon /> Locked
              </span>
            ) : (
              <button
                type="button"
                className="reward-card-pill reward-card-pill-apply"
                disabled={applying}
                onClick={() => onApply(offer)}
                aria-label={`Apply ${offer.name}`}
              >
                {applying ? <Spinner /> : "Apply"}
              </button>
            )}
          </div>

          {offer.description && <p className="reward-card-desc">{offer.description}</p>}
          <div className="reward-card-divider" />

          <div className="reward-card-cost">
            <CoinIcon size={26} />
            <strong>{offer.cost}</strong>
            <span>Circle Coins</span>
          </div>

        </div>

        {offer.animalIcon && <span className="reward-card-animal" style={tint(offer.animalIcon)} aria-hidden="true" />}
      </div>

      {/* Same-height status strip on every card, so the list stays even and the answer to
          "can I use this?" is always in the same place: ready / why it's locked / applied. */}
      <div
        className={`reward-card-status reward-card-status-${applied ? "applied" : lock ? `locked-${lock.kind}` : "ready"}`}
        role={lock?.kind === "coins" ? "progressbar" : undefined}
        aria-valuemin={lock?.kind === "coins" ? 0 : undefined}
        aria-valuemax={lock?.kind === "coins" ? lock.cost : undefined}
        aria-valuenow={lock?.kind === "coins" ? lock.balance : undefined}
        aria-label={lock?.kind === "coins" ? `${lock.balance} of ${lock.cost} Circle Coins` : undefined}
      >
        <span className="reward-card-status-icon" aria-hidden="true">
          {lock?.kind === "coins" ? <CoinIcon size={18} /> : lock ? <BagIcon /> : <CheckIcon />}
        </span>
        <span className="reward-card-status-text">
          {applied
            ? saving > 0 ? `Applied · you save ₹${saving}` : "Applied to this order"
            : lock?.kind === "coins"
            ? <><strong>{lock.need} more Circle Coins</strong> to unlock</>
            : lock
            ? lock.text
            : "Ready to use"}
        </span>
        {lock?.kind === "coins" && <span className="reward-card-status-count">{lock.balance}/{lock.cost}</span>}
        {lock?.kind === "coins" && <span className="reward-card-status-bar" style={{ width: `${lock.percent}%` }} />}
      </div>

      {applied && (
        <button type="button" className="reward-card-remove" onClick={() => onRemove(offer)} disabled={applying}>
          {applying ? <><Spinner /> Removing…</> : <><span aria-hidden="true">✕</span> Remove reward</>}
        </button>
      )}
    </div>
  );
}

export function RewardCardSkeleton() {
  return (
    <div className="reward-card reward-card-skeleton" aria-hidden="true">
      <div className="reward-card-main">
        <div className="reward-card-strip" />
        <div className="reward-card-body">
          <span className="reward-skel reward-skel-title" />
          <span className="reward-skel reward-skel-line" />
          <span className="reward-skel reward-skel-cost" />
        </div>
      </div>
    </div>
  );
}
